import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { ArrowLeft } from 'lucide-react';
import { motion } from 'framer-motion';
import Logo from '@/components/Logo';

const PrivacyPage = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-blue-950 to-slate-900">
      {/* Header */}
      <motion.header 
        initial={{ y: -100, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.6 }}
        className="glass-card border-b border-white/10 sticky top-0 z-50"
      >
        <div className="container mx-auto px-6 py-4 flex justify-between items-center">
          <Logo textClassName="text-2xl font-bold text-white" />
          <Button 
            variant="ghost" 
            onClick={() => navigate('/')}
            className="text-white hover:text-blue-300 hover:bg-white/10 transition-all"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Retour
          </Button>
        </div>
      </motion.header>

      {/* Content */}
      <div className="container mx-auto px-6 py-16 max-w-4xl">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="glass-card-white p-8 md:p-12 rounded-2xl"
        >
          <h1 className="text-4xl font-bold text-gray-900 mb-4">Politique de Confidentialité</h1>
          <p className="text-gray-600 mb-8">Dernière mise à jour : Décembre 2024</p>

          <div className="space-y-8 text-gray-700 leading-relaxed">
            {/* Intro */}
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">1. Introduction</h2>
              <p className="mb-4">
                MY DIGITAL IDENTITY LTD, opérant sous le nom commercial "Markeap", s'engage à protéger votre vie 
                privée. Cette Politique de Confidentialité explique comment nous collectons, utilisons, partageons 
                et protégeons vos informations personnelles.
              </p>
              <div className="bg-blue-50 p-6 rounded-xl border border-blue-100">
                <p className="font-semibold text-gray-900 mb-2">Responsable du traitement :</p>
                <p className="text-gray-700">MY DIGITAL IDENTITY LTD</p>
                <p className="text-gray-700">71-75 Shelton Street, Covent Garden</p>
                <p className="text-gray-700">London, United Kingdom, WC2H 9JQ</p>
              </div>
            </section>

            {/* Data Collection */}
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">2. Données Collectées</h2>
              
              <h3 className="text-xl font-semibold text-gray-900 mt-6 mb-3">2.1 Informations que vous nous fournissez</h3>
              <ul className="list-disc list-inside space-y-2 ml-4 mb-4">
                <li><strong>Informations de compte :</strong> Nom de l'entreprise, nom du propriétaire, email, numéro de téléphone</li>
                <li><strong>Informations de paiement :</strong> Détails de carte bancaire (traités de manière sécurisée via Stripe)</li>
                <li><strong>Données clients :</strong> Informations sur vos clients que vous importez dans la plateforme</li>
                <li><strong>Contenu :</strong> Messages marketing, réponses aux avis, configurations de jeux</li>
              </ul>

              <h3 className="text-xl font-semibold text-gray-900 mt-6 mb-3">2.2 Données collectées automatiquement</h3>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li><strong>Données d'utilisation :</strong> Pages visitées, fonctionnalités utilisées, durée des sessions</li>
                <li><strong>Données techniques :</strong> Adresse IP, type de navigateur, système d'exploitation, identifiant d'appareil</li>
                <li><strong>Cookies :</strong> Nous utilisons des cookies pour améliorer votre expérience (voir section Cookies)</li>
              </ul>
            </section>

            {/* Usage */}
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">3. Utilisation des Données</h2>
              <p className="mb-4">Nous utilisons vos données pour :</p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li>Fournir et maintenir le Service Markeap</li>
                <li>Traiter vos paiements et gérer votre abonnement</li>
                <li>Envoyer des SMS et emails marketing en votre nom</li>
                <li>Générer des réponses IA aux avis de vos clients</li>
                <li>Optimiser votre référencement SEO</li>
                <li>Vous contacter concernant votre compte ou des mises à jour du service</li>
                <li>Analyser et améliorer notre plateforme</li>
                <li>Détecter et prévenir les fraudes ou abus</li>
                <li>Respecter nos obligations légales</li>
              </ul>
            </section>

            {/* Base légale */}
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">4. Base Légale du Traitement (RGPD)</h2>
              <p className="mb-4">Nous traitons vos données personnelles sur les bases légales suivantes :</p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li><strong>Exécution du contrat :</strong> Pour fournir le service que vous avez commandé</li>
                <li><strong>Consentement :</strong> Pour les communications marketing (que vous pouvez retirer à tout moment)</li>
                <li><strong>Intérêts légitimes :</strong> Pour améliorer notre service, prévenir la fraude</li>
                <li><strong>Obligations légales :</strong> Pour respecter les lois fiscales et comptables</li>
              </ul>
            </section>

            {/* Sharing */}
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">5. Partage des Données</h2>
              <p className="mb-4">Nous ne vendons jamais vos données personnelles. Nous partageons vos données uniquement avec :</p>
              
              <h3 className="text-xl font-semibold text-gray-900 mt-6 mb-3">5.1 Fournisseurs de services</h3>
              <ul className="list-disc list-inside space-y-2 ml-4 mb-4">
                <li><strong>Firebase/Google :</strong> Authentification et base de données</li>
                <li><strong>Stripe :</strong> Traitement des paiements</li>
                <li><strong>Brevo :</strong> Envoi de SMS marketing</li>
                <li><strong>OpenAI :</strong> Génération de réponses IA</li>
              </ul>

              <h3 className="text-xl font-semibold text-gray-900 mt-6 mb-3">5.2 Obligations légales</h3>
              <p>Nous pouvons divulguer vos données si requis par la loi ou pour protéger nos droits légaux.</p>
            </section>

            {/* Storage */}
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">6. Conservation des Données</h2>
              <p>
                Nous conservons vos données personnelles aussi longtemps que nécessaire pour fournir le Service 
                et respecter nos obligations légales. Après la suppression de votre compte, vos données sont 
                supprimées dans un délai de 90 jours, sauf obligation légale de conservation plus longue.
              </p>
            </section>

            {/* Security */}
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">7. Sécurité des Données</h2>
              <p className="mb-4">Nous mettons en œuvre des mesures de sécurité appropriées :</p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li>Chiffrement SSL/TLS pour toutes les transmissions</li>
                <li>Stockage sécurisé dans des datacenters certifiés</li>
                <li>Contrôles d'accès stricts</li>
                <li>Sauvegardes régulières</li>
                <li>Surveillance des menaces de sécurité</li>
              </ul>
            </section>

            {/* Rights */}
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">8. Vos Droits (RGPD)</h2>
              <p className="mb-4">Vous disposez des droits suivants concernant vos données personnelles :</p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li><strong>Droit d'accès :</strong> Obtenir une copie de vos données personnelles</li>
                <li><strong>Droit de rectification :</strong> Corriger des données inexactes</li>
                <li><strong>Droit à l'effacement :</strong> Demander la suppression de vos données</li>
                <li><strong>Droit à la limitation :</strong> Limiter le traitement de vos données</li>
                <li><strong>Droit à la portabilité :</strong> Recevoir vos données dans un format structuré</li>
                <li><strong>Droit d'opposition :</strong> S'opposer au traitement de vos données</li>
                <li><strong>Droit de retirer le consentement :</strong> Retirer votre consentement à tout moment</li>
              </ul>
              <p className="mt-4">
                Pour exercer ces droits, contactez-nous via votre tableau de bord ou à l'adresse indiquée ci-dessous.
              </p>
            </section>

            {/* Cookies */}
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">9. Cookies</h2>
              <p className="mb-4">
                Nous utilisons des cookies essentiels pour le fonctionnement du service (authentification, préférences) 
                et des cookies analytiques pour comprendre comment vous utilisez Markeap. Vous pouvez désactiver les 
                cookies non essentiels dans les paramètres de votre navigateur.
              </p>
            </section>

            {/* Transfers */}
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">10. Transferts Internationaux</h2>
              <p>
                Vos données peuvent être transférées et stockées dans des pays en dehors de l'UE (notamment via 
                Firebase et nos autres fournisseurs cloud). Ces transferts sont effectués conformément au RGPD 
                via des clauses contractuelles types ou d'autres mécanismes appropriés.
              </p>
            </section>

            {/* Children */}
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">11. Protection des Mineurs</h2>
              <p>
                Markeap n'est pas destiné aux personnes de moins de 18 ans. Nous ne collectons pas sciemment 
                de données personnelles auprès de mineurs. Si vous pensez qu'un mineur nous a fourni des données, 
                contactez-nous immédiatement.
              </p>
            </section>

            {/* Changes */}
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">12. Modifications de cette Politique</h2>
              <p>
                Nous pouvons mettre à jour cette Politique de Confidentialité périodiquement. Les modifications 
                substantielles vous seront notifiées par email. La date de "Dernière mise à jour" en haut de cette 
                page indique quand la politique a été révisée pour la dernière fois.
              </p>
            </section>

            {/* Contact */}
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">13. Nous Contacter</h2>
              <p className="mb-4">
                Pour toute question concernant cette Politique de Confidentialité ou pour exercer vos droits :
              </p>
              <div className="bg-gray-50 p-6 rounded-xl border border-gray-200">
                <p className="font-semibold text-gray-900">MY DIGITAL IDENTITY LTD (Markeap)</p>
                <p className="text-gray-700">71-75 Shelton Street, Covent Garden</p>
                <p className="text-gray-700">London, United Kingdom, WC2H 9JQ</p>
              </div>
              <p className="mt-4 text-sm text-gray-600">
                Vous avez également le droit de déposer une plainte auprès de l'autorité de protection des données 
                de votre pays (ICO au Royaume-Uni, CNIL en France).
              </p>
            </section>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default PrivacyPage;
